import type { ServerTourEntry, ServerTourStatus } from '@/shared/stores/auth.store'
import type { Tour, TourAccessContext, TourStep } from '../types'
import { startSteps } from './tour-runner'
import { getTourActivator } from '../stores/walkthrough.store'
import { loadProgress } from './progress-storage'
import { isImpersonating } from './impersonation'

/**
 * Progressive page onboarding — pure rules + per-browser state.
 *
 * Status is keyed by STABLE tour id (dynamic routes → one id; the matcher uses
 * the pathname only, so query strings never matter). The server map
 * (/auth/me onboarding.tours) is authoritative; a local fallback keeps an
 * answer when a write fails, and old localStorage progress is carried over.
 */

export type InviteKind = 'card' | 'beacon' | 'off'

/** Answered statuses never re-offer. ('offered' only ends a beacon.) */
const ANSWERED: ServerTourStatus[] = ['started', 'completed', 'skipped', 'dismissed']

/** Card cooldown between two page cards (any tours), per user and session. */
export const PAGE_CARD_COOLDOWN_MS = 3 * 60 * 1000
/** Shown in this many sessions without an answer → recorded as dismissed. */
export const MAX_PAGE_CARD_SESSIONS = 2
/** User must have been idle this long before a card appears. */
export const INVITE_IDLE_MS = 2000
/** A page gives up offering after this long per visit. */
export const INVITE_GIVE_UP_MS = 30_000

export function inviteKindFor(tour: Tour): InviteKind {
  if (tour.kind !== 'page') return 'off'
  return tour.invite ?? (tour.mayHoldEdits ? 'beacon' : 'card')
}

/** The page state a tour reads (`tour.state`) — the runner's `readState`. */
export function readDomState(spec: NonNullable<Tour['state']>): string | null {
  return document.querySelector(spec.selector)?.getAttribute(spec.attribute) ?? null
}

/** The steps a run started now would have: the runner's own `startSteps`. */
export function eligibleInviteSteps(tour: Tour, ctx: TourAccessContext, tours: readonly Tour[], path: string): TourStep[] {
  return startSteps(tour, ctx, tours, path, tour.state ? readDomState(tour.state) : null, getTourActivator)
}

// ── Local fallback (per user): answers that couldn't be written + view counts ──

type LocalPages = Record<string, { s?: ServerTourStatus; views?: number }>
const pagesKey = (userId: string) => `nxtgen.onboarding.v1.pages.${userId}`
const sessionKey = (userId: string) => `nxtgen.onboarding.v1.pages-session.${userId}`

export function loadLocalPages(userId: string): LocalPages {
  try {
    const raw = localStorage.getItem(pagesKey(userId))
    const v: unknown = raw ? JSON.parse(raw) : {}
    return v && typeof v === 'object' && !Array.isArray(v) ? (v as LocalPages) : {}
  } catch {
    return {}
  }
}

export function saveLocalPage(userId: string, tourId: string, patch: { s?: ServerTourStatus; views?: number }): void {
  if (isImpersonating()) return // never on the impersonator's browser
  try {
    const all = loadLocalPages(userId)
    all[tourId] = { ...all[tourId], ...patch }
    localStorage.setItem(pagesKey(userId), JSON.stringify(all))
  } catch {
    /* storage unavailable — the server status still governs */
  }
}

interface SessionState {
  lastCardAt?: number
  /** Tours already counted as shown in this session. */
  counted?: string[]
}

export function loadSession(userId: string): SessionState {
  try {
    const raw = sessionStorage.getItem(sessionKey(userId))
    return raw ? (JSON.parse(raw) as SessionState) : {}
  } catch {
    return {}
  }
}

export function saveSession(userId: string, s: SessionState): void {
  if (isImpersonating()) return // never on the impersonator's browser
  try {
    sessionStorage.setItem(sessionKey(userId), JSON.stringify(s))
  } catch {
    /* ignore */
  }
}

/**
 * The status that decides whether a tour is still offered: server first, then
 * the local fallback, then old localStorage tour progress (completed/skipped in
 * any version — carried over lazily by the caller).
 */
export function answeredStatus(
  tourId: string,
  server: Record<string, ServerTourEntry> | undefined,
  userId: string,
): { status: ServerTourStatus | null; carryOver: ServerTourStatus | null } {
  const srv = server?.[tourId]?.s ?? null
  if (srv) return { status: srv, carryOver: null }
  const local = loadLocalPages(userId)[tourId]?.s ?? null
  if (local) return { status: local, carryOver: null }
  const legacy = loadProgress(userId)[tourId]?.status
  if (legacy === 'completed' || legacy === 'skipped') return { status: legacy, carryOver: legacy }
  return { status: null, carryOver: null }
}

export const isAnswered = (s: ServerTourStatus | null) => !!s && ANSWERED.includes(s)

export interface InviteContext {
  kind: InviteKind
  status: ServerTourStatus | null
  eligibleSteps: number
  pageInvites: boolean
  impersonating: boolean
  /** Welcome card eligible-and-unanswered, or on screen. */
  welcomePending: boolean
  tourRunning: boolean
  resumePending: boolean
  now: number
  lastCardAt: number | undefined
  lastActivityAt: number
  focusInField: boolean
  dialogOpen: boolean
  helpMenuOpen: boolean
  pageUnavailable: boolean
  firstTargetPresent: boolean
  settled: boolean
  /** For `invitePristineOnly` tours. */
  dirty: boolean
  pristineOnly: boolean
}

export type InviteDecision = 'card' | 'beacon' | 'none'

/** Every rule must hold. Pure: the hook feeds it DOM / store facts each second. */
export function decideInvite(c: InviteContext): InviteDecision {
  if (c.impersonating || c.kind === 'off' || c.eligibleSteps < 2) return 'none'
  // Opted out ("Don't suggest page tours") = no card AND no beacon; ? still lists the guide.
  if (!c.pageInvites || c.tourRunning || c.resumePending) return 'none'
  if (c.kind === 'beacon') {
    // Passive: shown until opened (offered) or answered.
    return c.status ? 'none' : 'beacon'
  }
  if (isAnswered(c.status) || c.welcomePending) return 'none'
  if (c.lastCardAt !== undefined && c.now - c.lastCardAt < PAGE_CARD_COOLDOWN_MS) return 'none'
  if (!c.settled || !c.firstTargetPresent || c.pageUnavailable) return 'none'
  if (c.now - c.lastActivityAt < INVITE_IDLE_MS || c.focusInField) return 'none'
  if (c.dialogOpen || c.helpMenuOpen) return 'none'
  if (c.pristineOnly && c.dirty) return 'none'
  return 'card'
}

/** Is the focused element a place where the user is typing? */
export function focusInField(el: Element | null): boolean {
  if (!el || !(el instanceof HTMLElement)) return false
  return el.matches('input, textarea, select, [contenteditable=""], [contenteditable="true"]')
}
